import { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { botService } from '@/services/bot'
import { formatPrice } from '@/utils/formatters'

interface GridConfig {
  precio_superior: number
  precio_inferior: number
  numero_grids: number
  tipo_grid: 'aritmetico' | 'geometrico'
  capital_total: number
}

interface RiskConfig {
  porcentaje_apuesta_kelly: number
  trailing_stop_loss: number
  take_profit: number
  max_drawdown_permitido: number
}

interface MarketConfig {
  exchange_id: string
  par_operacion: string
  timeframe: string
  comision_exchange: number
  testnet: boolean
  modo_dry_run: boolean
}

interface ExchangeCredentials {
  exchange_id: string
  api_key: string
  api_secret: string
  testnet: boolean
  passphrase?: string
}

interface ExchangeCredentialsResponse {
  exchange_id: string
  api_key_masked: string
  testnet: boolean
  has_passphrase: boolean
  is_configured: boolean
}

const ConfigurationPage = () => {
  const [gridConfig, setGridConfig] = useState<GridConfig>({
    precio_superior: 105000,
    precio_inferior: 100000,
    numero_grids: 10,
    tipo_grid: 'aritmetico',
    capital_total: 10000
  })

  const [riskConfig, setRiskConfig] = useState<RiskConfig>({
    porcentaje_apuesta_kelly: 0.02,
    trailing_stop_loss: 0.03,
    take_profit: 0.05,
    max_drawdown_permitido: 0.15
  })

  const [marketConfig, setMarketConfig] = useState<MarketConfig>({
    exchange_id: 'deribit',
    par_operacion: 'BTC/USDT',
    timeframe: '15m',
    comision_exchange: 0.001,
    testnet: true,
    modo_dry_run: true
  })

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState('')

  // Estado para gestión de credenciales de exchanges
  const [exchangeCredentials, setExchangeCredentials] = useState<ExchangeCredentialsResponse[]>([])
  const [newCredentials, setNewCredentials] = useState<ExchangeCredentials>({
    exchange_id: 'binance',
    api_key: '',
    api_secret: '',
    testnet: true,
    passphrase: ''
  })
  const [showCredentialsForm, setShowCredentialsForm] = useState(false)
  const [testingConnection, setTestingConnection] = useState('')

  useEffect(() => {
    loadConfigurations()
    loadExchangeCredentials()
  }, [])

  const loadConfigurations = async () => {
    setLoading(true)
    try {
      const [gridData, riskData, marketData] = await Promise.all([
        botService.getGridConfig(),
        botService.getRiskConfig(),
        botService.getMarketConfig()
      ])
      setGridConfig(gridData)
      setRiskConfig(riskData)
      setMarketConfig(marketData)
    } catch (error) {
      console.error('Error loading configurations:', error)
      toast.error('Error al cargar configuraciones')
    } finally {
      setLoading(false)
    }
  }

  const loadExchangeCredentials = async () => {
    try {
      const credentials = await botService.getExchangeCredentials()
      setExchangeCredentials(credentials)
    } catch (error) {
      console.error('Error loading exchange credentials:', error)
      // No mostrar error si no hay credenciales configuradas
    }
  }

  const saveGridConfig = async () => {
    setSaving('grid')
    try {
      await botService.updateGridConfig(gridConfig)
      toast.success('Configuración de Grid guardada')
    } catch (error) {
      console.error('Error saving grid config:', error)
      toast.error('Error al guardar configuración de Grid')
    } finally {
      setSaving('')
    }
  }

  const saveRiskConfig = async () => {
    setSaving('risk')
    try {
      await botService.updateRiskConfig(riskConfig)
      toast.success('Configuración de Riesgo guardada')
    } catch (error) {
      console.error('Error saving risk config:', error)
      toast.error('Error al guardar configuración de Riesgo')
    } finally {
      setSaving('')
    }
  }

  const saveMarketConfig = async () => {
    setSaving('market')
    try {
      await botService.updateMarketConfig(marketConfig)
      toast.success('Configuración de Mercado guardada')
    } catch (error) {
      console.error('Error saving market config:', error)
      toast.error('Error al guardar configuración de Mercado')
    } finally {
      setSaving('')
    }
  }

  // Funciones para manejo de credenciales de exchanges
  const saveExchangeCredentials = async () => {
    setSaving('credentials')
    try {
      await botService.saveExchangeCredentials(newCredentials)
      toast.success(`Credenciales de ${newCredentials.exchange_id} guardadas`)
      setNewCredentials({
        exchange_id: 'binance',
        api_key: '',
        api_secret: '',
        testnet: true,
        passphrase: ''
      })
      setShowCredentialsForm(false)
      await loadExchangeCredentials()
    } catch (error) {
      console.error('Error saving credentials:', error)
      toast.error('Error al guardar credenciales')
    } finally {
      setSaving('')
    }
  }

  const testConnection = async (exchangeId: string) => {
    setTestingConnection(exchangeId)
    try {
      const result = await botService.testExchangeConnection(exchangeId)
      if (result.connection_success) {
        toast.success(`Conexión exitosa con ${exchangeId}`)
      } else {
        toast.error(`Error conectando con ${exchangeId}: ${result.error_message}`)
      }
    } catch (error) {
      console.error('Error testing connection:', error)
      toast.error('Error al probar conexión')
    } finally {
      setTestingConnection('')
    }
  }

  const deleteCredentials = async (exchangeId: string) => {
    if (!confirm(`¿Estás seguro de eliminar las credenciales de ${exchangeId}?`)) {
      return
    }
    
    try {
      await botService.deleteExchangeCredentials(exchangeId)
      toast.success(`Credenciales de ${exchangeId} eliminadas`)
      await loadExchangeCredentials()
    } catch (error) {
      console.error('Error deleting credentials:', error)
      toast.error('Error al eliminar credenciales')
    }
  }

  const reinitializeGrid = async () => {
    if (!confirm('¿Estás seguro de que quieres reinicializar el grid? Esto cancelará todas las órdenes abiertas.')) {
      return
    }
    
    setSaving('reinit')
    try {
      const result = await botService.reinitializeGrid()
      toast.success(`Grid reinicializado con ${result.niveles_creados} niveles`)
    } catch (error) {
      console.error('Error reinitializing grid:', error)
      toast.error('Error al reinicializar grid')
    } finally {
      setSaving('')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">Configuración del Grid Trading Bot</h1>
        <p className="text-gray-600 mt-2">
          Configura los parámetros del grid, gestión de riesgo y configuración de mercado
        </p>
      </div>

      {/* Grid Configuration */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">📊 Configuración del Grid</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Precio Superior (USDT)
              </label>
              <input
                type="number"
                value={gridConfig.precio_superior}
                onChange={(e) => setGridConfig({...gridConfig, precio_superior: Number(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Precio Inferior (USDT)
              </label>
              <input
                type="number"
                value={gridConfig.precio_inferior}
                onChange={(e) => setGridConfig({...gridConfig, precio_inferior: Number(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de Grids
              </label>
              <input
                type="number"
                value={gridConfig.numero_grids}
                onChange={(e) => setGridConfig({...gridConfig, numero_grids: Number(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="2"
                max="100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Grid
              </label>
              <select
                value={gridConfig.tipo_grid}
                onChange={(e) => setGridConfig({...gridConfig, tipo_grid: e.target.value as 'aritmetico' | 'geometrico'})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="aritmetico">Aritmético</option>
                <option value="geometrico">Geométrico</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Capital Total (USDT)
              </label>
              <input
                type="number"
                value={gridConfig.capital_total}
                onChange={(e) => setGridConfig({...gridConfig, capital_total: Number(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={saveGridConfig}
              disabled={saving === 'grid'}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving === 'grid' ? 'Guardando...' : 'Guardar Configuración'}
            </button>

            <button
              onClick={reinitializeGrid}
              disabled={saving === 'reinit'}
              className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
            >
              {saving === 'reinit' ? 'Reinicializando...' : 'Reinicializar Grid'}
            </button>
          </div>
        </div>
      </div>

      {/* Risk Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">⚠️ Gestión de Riesgo</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kelly Percentage (%)
              </label>
              <input
                type="number"
                value={riskConfig.porcentaje_apuesta_kelly * 100}
                onChange={(e) => setRiskConfig({...riskConfig, porcentaje_apuesta_kelly: Number(e.target.value) / 100})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                max="100"
              />
              <p className="text-sm text-gray-500 mt-1">Porcentaje de capital por operación</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trailing Stop Loss (%)
              </label>
              <input
                type="number"
                value={riskConfig.trailing_stop_loss * 100}
                onChange={(e) => setRiskConfig({...riskConfig, trailing_stop_loss: Number(e.target.value) / 100})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                max="100"
              />
              <p className="text-sm text-gray-500 mt-1">Stop loss dinámico</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Take Profit (%)
              </label>
              <input
                type="number"
                value={riskConfig.take_profit * 100}
                onChange={(e) => setRiskConfig({...riskConfig, take_profit: Number(e.target.value) / 100})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                max="100"
              />
              <p className="text-sm text-gray-500 mt-1">Objetivo de ganancia</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Drawdown (%)
              </label>
              <input
                type="number"
                value={riskConfig.max_drawdown_permitido * 100}
                onChange={(e) => setRiskConfig({...riskConfig, max_drawdown_permitido: Number(e.target.value) / 100})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                max="100"
              />
              <p className="text-sm text-gray-500 mt-1">Máxima pérdida permitida</p>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={saveRiskConfig}
              disabled={saving === 'risk'}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {saving === 'risk' ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        </div>
      </div>

      {/* Market Configuration */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">🏪 Configuración de Mercado</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Exchange
              </label>
              <select
                value={marketConfig.exchange_id}
                onChange={(e) => setMarketConfig({...marketConfig, exchange_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="deribit">Deribit</option>
                <option value="binance">Binance</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">Selecciona el exchange que vas a usar</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Par de Operación
              </label>
              <input
                type="text"
                value={marketConfig.par_operacion}
                onChange={(e) => setMarketConfig({...marketConfig, par_operacion: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="BTC/USDT"
              />
              <p className="text-sm text-gray-500 mt-1">Formato: BASE/QUOTE (ej: BTC/USDT, ETH/BTC)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timeframe
              </label>
              <select
                value={marketConfig.timeframe}
                onChange={(e) => setMarketConfig({...marketConfig, timeframe: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="1m">1 minuto</option>
                <option value="5m">5 minutos</option>
                <option value="15m">15 minutos</option>
                <option value="30m">30 minutos</option>
                <option value="1h">1 hora</option>
                <option value="2h">2 horas</option>
                <option value="4h">4 horas</option>
                <option value="6h">6 horas</option>
                <option value="12h">12 horas</option>
                <option value="1d">1 día</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">Intervalo de tiempo para el análisis</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comisión Exchange (%)
              </label>
              <input
                type="number"
                value={marketConfig.comision_exchange * 100}
                onChange={(e) => setMarketConfig({...marketConfig, comision_exchange: Number(e.target.value) / 100})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.001"
                min="0"
                max="5"
                placeholder="0.1"
              />
              <p className="text-sm text-gray-500 mt-1">Comisión que cobra tu exchange por transacción</p>
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="testnet"
                  checked={marketConfig.testnet}
                  onChange={(e) => setMarketConfig({...marketConfig, testnet: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="testnet" className="ml-2 text-sm text-gray-700">
                  Testnet / Paper Trading
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="dry-run"
                  checked={marketConfig.modo_dry_run}
                  onChange={(e) => setMarketConfig({...marketConfig, modo_dry_run: e.target.checked})}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="dry-run" className="ml-2 text-sm text-gray-700">
                  Modo Demo (Sin operaciones reales)
                </label>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-blue-500">💡</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Recomendación de Seguridad
                  </h3>
                  <p className="mt-1 text-sm text-blue-700">
                    Te recomendamos usar el <strong>Modo Demo</strong> o <strong>Testnet</strong> primero para probar 
                    tu estrategia antes de usar dinero real. Esto te permite verificar que todo funciona correctamente.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={saveMarketConfig}
              disabled={saving === 'market'}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            >
              {saving === 'market' ? 'Guardando...' : 'Guardar Configuración de Mercado'}
            </button>
          </div>
        </div>
      </div>

      {/* Exchange Credentials Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">🔐 Gestión de Credenciales de Exchanges</h2>
        </div>
        <div className="p-6">
          {/* Exchanges configurados */}
          {exchangeCredentials.length > 0 && (
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-700 mb-4">Exchanges Configurados</h3>
              <div className="space-y-3">
                {exchangeCredentials.map((cred) => (
                  <div key={cred.exchange_id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {cred.exchange_id === 'binance' && <span className="text-2xl">🟡</span>}
                        {cred.exchange_id === 'deribit' && <span className="text-2xl">🟦</span>}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 capitalize">{cred.exchange_id}</h4>
                        <p className="text-sm text-gray-500">
                          API Key: {cred.api_key_masked} | {cred.testnet ? 'Testnet' : 'Mainnet'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => testConnection(cred.exchange_id)}
                        disabled={testingConnection === cred.exchange_id}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {testingConnection === cred.exchange_id ? 'Probando...' : 'Probar'}
                      </button>
                      <button
                        onClick={() => deleteCredentials(cred.exchange_id)}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Formulario para agregar credenciales */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-medium text-gray-700">Agregar Nuevo Exchange</h3>
              <button
                onClick={() => setShowCredentialsForm(!showCredentialsForm)}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {showCredentialsForm ? 'Cancelar' : '+ Agregar Exchange'}
              </button>
            </div>

            {showCredentialsForm && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Exchange
                    </label>
                    <select
                      value={newCredentials.exchange_id}
                      onChange={(e) => setNewCredentials({...newCredentials, exchange_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="binance">🟡 Binance</option>
                      <option value="deribit">🟦 Deribit</option>
                    </select>
                  </div>

                  <div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="testnet"
                        checked={newCredentials.testnet}
                        onChange={(e) => setNewCredentials({...newCredentials, testnet: e.target.checked})}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="testnet" className="ml-2 text-sm text-gray-700">
                        Usar Testnet (Recomendado)
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key
                  </label>
                  <input
                    type="text"
                    value={newCredentials.api_key}
                    onChange={(e) => setNewCredentials({...newCredentials, api_key: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Tu API Key del exchange"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Secret
                  </label>
                  <input
                    type="password"
                    value={newCredentials.api_secret}
                    onChange={(e) => setNewCredentials({...newCredentials, api_secret: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Tu API Secret del exchange"
                  />
                </div>

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-yellow-500">⚠️</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Importante - Seguridad
                      </h3>
                      <p className="mt-1 text-sm text-yellow-700">
                        • Usa <strong>Testnet</strong> primero para probar<br />
                        • Solo da permisos de <strong>trading</strong> (no withdrawal)<br />
                        • Las credenciales se almacenan de forma segura<br />
                        • Puedes revocar el acceso desde tu exchange en cualquier momento
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={saveExchangeCredentials}
                    disabled={saving === 'credentials' || !newCredentials.api_key || !newCredentials.api_secret}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {saving === 'credentials' ? 'Guardando...' : 'Guardar Credenciales'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCredentialsForm(false)
                      setNewCredentials({
                        exchange_id: 'binance',
                        api_key: '',
                        api_secret: '',
                        testnet: true,
                        passphrase: ''
                      })
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Grid Preview */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">👁️ Vista Previa del Grid</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-700">Rango de Precios</div>
              <div className="text-2xl font-bold text-blue-900">
                ${gridConfig.precio_inferior.toLocaleString()} - ${gridConfig.precio_superior.toLocaleString()}
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-green-700">Separación por Grid</div>
              <div className="text-2xl font-bold text-green-900">
                {formatPrice((gridConfig.precio_superior - gridConfig.precio_inferior) / gridConfig.numero_grids, 2)}
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-purple-700">Capital por Nivel</div>
              <div className="text-2xl font-bold text-purple-900">
                {formatPrice(gridConfig.capital_total / gridConfig.numero_grids, 2)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfigurationPage